<script lang="ts">
  import { ApiError, geocodeZip, searchPlaces } from '../api/client'
  import { select } from '../state/location.svelte'
  import Icon from './Icon.svelte'

  interface Props {
    /** A place was found: a layout may change section (Places and Settings open Forecast). */
    onResolved?: (source: 'search' | 'geo') => void
    /**
     * `float` is the desktop's search over the map (Phase 11 plan §7): a
     * 48 px card on the float shadow, a search icon, the field and the `/`
     * that focuses it from anywhere on the page. `panel` is the phone's search panel
     * as round 2 draws it (3.6): a search icon, the field and a clear cell,
     * and under it the matches, looked up as they are typed. `start` is the
     * first run with location blocked (3.7): a field and a worded "Show
     * forecast" button. The matches are a list under the field in `panel`
     * and a list dropped over what follows in the other two.
     */
    variant?: 'panel' | 'float' | 'start'
  }

  let { onResolved, variant = 'panel' }: Props = $props()

  const uid = $props.id()
  const listId = `${uid}-matches`

  /** Five digits are a ZIP; anything else with two letters in it is a name. */
  const ZIP_RE = /^\d{5}$/
  const DIGITS_RE = /^\d+$/
  const MIN_NAME = 2
  /** Long enough that a word typed at speed is one request, short enough to feel live. */
  const DEBOUNCE_MS = 120

  /**
   * One row under the field. A ZIP comes back as one place from
   * zippopotam.us; a name as up to eight from our own Census table
   * (`/api/geocode/search`), best match first.
   */
  interface Hit {
    lat: number
    lon: number
    /** "Boulder, CO": what the header will call the point. */
    label: string
    /** The row's second line: the ZIP, or the state spelled out. */
    detail: string
    zip?: string
  }

  let query = $state('')
  let input: HTMLInputElement | undefined = $state()

  /**
   * `/` focuses the desktop's search from anywhere, as the mockup's hint says,
   * unless the key is being typed into something.
   */
  function onSlash(e: KeyboardEvent) {
    if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return
    const t = e.target as HTMLElement | null
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
    e.preventDefault()
    input?.focus()
  }
  let error = $state('')
  let busy = $state(false)

  /**
   * The matches for `for`, which is the trimmed query they answer. A `miss`
   * is a query nobody's place is called, which is a different sentence from
   * a network failure (that one says nothing until Go is pressed).
   */
  type Matches = { for: string; hits: Hit[] } | { for: string; miss: 'zip' | 'name' }
  let matches = $state<Matches | null>(null)
  let active = $state(0)
  /** The dropped list closes when focus leaves the search; typing reopens it. */
  let dismissed = $state(false)
  let lookupSeq = 0

  let hits = $derived(matches && 'hits' in matches ? matches.hits : [])
  let miss = $derived(matches && 'miss' in matches ? matches : null)
  let showList = $derived(hits.length > 0 && !dismissed)

  async function lookup(value: string, signal?: AbortSignal): Promise<Matches> {
    if (ZIP_RE.test(value)) {
      try {
        const { lat, lon, city, state } = await geocodeZip(value)
        const label = [city, state].filter(Boolean).join(', ') || `ZIP ${value}`
        return { for: value, hits: [{ lat, lon, label, detail: `ZIP ${value}`, zip: value }] }
      } catch (e) {
        // Only a 404 says the ZIP is not a place; anything else is the
        // network or the server, and Go will say which.
        if (e instanceof ApiError && e.status === 404) return { for: value, miss: 'zip' }
        throw e
      }
    }
    const found = await searchPlaces(value, signal)
    if (!found.length) return { for: value, miss: 'name' }
    return {
      for: value,
      hits: found.map((p) => ({
        lat: p.lat,
        lon: p.lon,
        label: `${p.name}, ${p.state}`,
        detail: p.state_name,
      })),
    }
  }

  function searchable(value: string): boolean {
    return ZIP_RE.test(value) || (!DIGITS_RE.test(value) && value.replace(/[^\p{L}]/gu, '').length >= MIN_NAME)
  }

  $effect(() => {
    const value = query.trim()
    const seq = ++lookupSeq
    if (!searchable(value)) {
      matches = null
      return
    }
    const controller = new AbortController()
    // A ZIP is looked up the moment the fifth digit lands, as it always was;
    // a name waits for a pause in the typing.
    const timer = setTimeout(
      () => {
        lookup(value, controller.signal).then(
          (found) => {
            if (seq !== lookupSeq) return
            matches = found
            active = 0
          },
          () => {
            if (seq === lookupSeq) matches = null
          },
        )
      },
      ZIP_RE.test(value) ? 0 : DEBOUNCE_MS,
    )
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  })

  async function open(hit: Hit) {
    // The header names the place now; a half-typed "portland," left in the
    // field would read as a search still under way.
    query = ''
    matches = null
    onResolved?.('search')
    await select({ lat: hit.lat, lon: hit.lon, label: hit.label, zip: hit.zip, source: 'search' })
  }

  function clear(e: MouseEvent & { currentTarget: HTMLElement }) {
    query = ''
    error = ''
    e.currentTarget.closest('form')?.querySelector('input')?.focus()
  }

  function onInput() {
    dismissed = false
    error = ''
  }

  function onKeydown(e: KeyboardEvent) {
    if (!hits.length) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      dismissed = false
      const step = e.key === 'ArrowDown' ? 1 : -1
      active = (active + step + hits.length) % hits.length
    } else if (e.key === 'Escape' && showList) {
      // Only the list: a second Escape reaches the phone panel and closes it.
      e.stopPropagation()
      dismissed = true
    }
  }

  function onFocusOut(e: FocusEvent & { currentTarget: HTMLElement }) {
    const next = e.relatedTarget as Node | null
    if (!next || !e.currentTarget.contains(next)) dismissed = true
  }

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault()
    const value = query.trim()
    error = ''
    // Go takes the highlighted row when the rows answer what is in the field.
    if (matches?.for === value && hits.length) {
      await open(hits[Math.min(active, hits.length - 1)])
      return
    }
    if (DIGITS_RE.test(value) && !ZIP_RE.test(value)) {
      error = 'A ZIP is five digits.'
      return
    }
    if (!searchable(value)) {
      error = 'Enter a place name or a ZIP.'
      return
    }
    busy = true
    try {
      const found = await lookup(value)
      if ('hits' in found) await open(found.hits[0])
      else error = missText(found)
    } catch (e) {
      error = e instanceof ApiError ? e.detail : `Network error: ${message(e)}`
    } finally {
      busy = false
    }
  }

  function missText(m: { for: string; miss: 'zip' | 'name' }): string {
    return m.miss === 'zip' ? `No place for ${m.for}` : `No place called “${m.for}”`
  }

  function message(e: unknown): string {
    return e instanceof Error ? e.message : String(e)
  }
</script>

<svelte:window onkeydown={variant === 'float' ? onSlash : undefined} />

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="searcher" data-variant={variant} onfocusout={onFocusOut}>
  <form class="search {variant}" onsubmit={onSubmit} autocomplete="off">
    {#if variant === 'panel' || variant === 'float'}
      <span class="lead" aria-hidden="true"><Icon name="search" size={20} /></span>
    {/if}
    <input
      bind:this={input}
      class="touch-target"
      type="text"
      role="combobox"
      enterkeyhint="search"
      autocapitalize="words"
      spellcheck="false"
      maxlength="80"
      placeholder={variant === 'start' ? '' : variant === 'float' ? 'Search a city or ZIP' : 'City, town or ZIP'}
      aria-label="Place or ZIP"
      aria-autocomplete="list"
      aria-controls={listId}
      aria-expanded={showList}
      aria-activedescendant={showList ? `${uid}-opt-${active}` : undefined}
      bind:value={query}
      oninput={onInput}
      onkeydown={onKeydown}
      disabled={busy}
    />
    {#if variant === 'float'}
      <kbd aria-hidden="true" title="Press / to search">/</kbd>
      <!-- Named "Go" as it always was; Enter is the way to press it. -->
      <button class="visually-hidden" type="submit" disabled={busy} tabindex="-1">Go</button>
    {:else if variant === 'start'}
      <button class="touch-target show" type="submit" disabled={busy}>Show forecast</button>
    {:else}
      <!-- The keyboard's return key submits; the rows below are the thing to
           tap. Go stays for a keyboard with no return key on it. -->
      {#if query}
        <button class="touch-target clear" type="button" aria-label="Clear" onclick={clear}>
          <Icon name="x" size={18} strokeWidth={2.5} />
        </button>
      {/if}
      <button class="visually-hidden" type="submit" disabled={busy} tabindex="-1">Go</button>
    {/if}
  </form>

  <div class="matches" id={listId} role="listbox" aria-label="Places" hidden={!showList}>
    {#each showList ? hits : [] as hit, i (`${hit.label} ${hit.lat} ${hit.lon}`)}
      <button
        type="button"
        role="option"
        id="{uid}-opt-{i}"
        class="match touch-target"
        class:active={i === active}
        aria-selected={i === active}
        tabindex="-1"
        onpointerenter={() => (active = i)}
        onclick={() => void open(hit)}
      >
        <Icon name="map-pin" size={20} />
        <span class="match-text">
          <span class="match-name">{hit.label}</span>
          <span class="match-sub">{hit.detail}</span>
        </span>
        {#if i === active}<Icon name="arrow-right" size={18} strokeWidth={2.5} />{/if}
      </button>
    {/each}
  </div>

  {#if variant === 'panel' && miss}
    <div class="miss" role="status">
      <span class="miss-title">{missText(miss)}</span>
      <span class="miss-sub">
        {miss.miss === 'zip'
          ? 'Check the five digits.'
          : 'Search takes US cities, towns and ZIP codes.'}
      </span>
    </div>
  {/if}

  {#if error}
    <p class="error" role="alert">{error}</p>
  {/if}
</div>

<style>
  .search {
    display: flex;
    gap: 6px;
  }

  button {
    border: 0;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.6;
    cursor: default;
  }

  /* ---- start: the first run (round 2's 3.7) ---- */
  .start {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0;
  }

  .start input {
    min-width: 0;
    height: var(--start-h, 44px);
    padding: 0 12px;
    border: 2px solid var(--color-text);
    border-right: 0;
    border-radius: 0;
    background: var(--color-bg);
    box-shadow: inset 0 -3px 0 var(--color-text);
    font-size: 16px;
  }

  .show {
    height: var(--start-h, 44px);
    padding: 0 16px;
    background: var(--color-text);
    color: var(--color-bg);
    font-size: 14px;
    font-weight: 800;
  }

  /* ---- panel: the phone's search (round 2's 3.6) ---- */
  .panel {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) 40px;
    align-items: center;
    gap: 0;
    height: 48px;
    border: 2px solid var(--color-text);
    box-shadow: inset 0 -3px 0 var(--color-text);
    background: var(--color-bg);
  }

  .lead {
    display: grid;
    place-items: center;
  }

  .panel input {
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    /* 16px stops iOS Safari zooming the viewport when the field is focused;
       17 is the handoff's. */
    font-size: 17px;
    font-weight: 800;
    letter-spacing: 0.04em;
    outline: none;
  }

  .panel input::placeholder {
    font-weight: 400;
    letter-spacing: 0;
    color: var(--color-muted);
  }

  .panel:focus-within {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  .clear {
    width: 40px;
    height: 44px;
    display: grid;
    place-items: center;
    padding: 0;
    background: none;
  }

  .searcher {
    position: relative;
  }

  .matches[hidden] {
    display: none;
  }

  .matches {
    display: flex;
    flex-direction: column;
    margin-top: 12px;
    border: 2px solid var(--color-text);
    background: var(--color-bg);
    color: var(--color-text);
  }

  /* In the hero and on the first run the list drops over what follows,
     rather than pushing the forecast down for as long as it is open. */
  .searcher[data-variant='float'] .matches,
  .searcher[data-variant='start'] .matches {
    position: absolute;
    z-index: 20;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    box-shadow: 0 6px 18px rgb(0 0 0 / 0.25);
  }

  .match {
    display: grid;
    grid-template-columns: 32px minmax(0, 1fr) 18px;
    align-items: center;
    width: 100%;
    min-height: 52px;
    padding: 0 12px;
    background: none;
    color: inherit;
    text-align: left;
  }

  .match + .match {
    border-top: 1px solid color-mix(in srgb, var(--color-text) 20%, transparent);
  }

  /* The row Go opens, in the inverse the single ZIP match always had. */
  .match.active {
    background: var(--color-text);
    color: var(--color-bg);
  }

  .match-text,
  .miss {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .match-name,
  .miss-title {
    font-size: 16px;
    font-weight: 800;
  }

  .match-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .match-sub {
    font-size: 13px;
    opacity: 0.75;
  }

  .miss {
    gap: 3px;
    margin-top: 12px;
    padding: 12px 14px;
    border: 2px solid var(--color-text);
  }

  .miss-sub {
    font-size: 13px;
    color: var(--color-muted);
  }

  /* ---- float: over the desktop's map (Phase 11 plan §7) ---- */
  .float {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto;
    align-items: center;
    gap: 0;
    height: 48px;
    padding: 0 10px 0 4px;
    background: var(--color-bg);
    box-shadow: var(--float-shadow);
    color: var(--color-text);
  }

  .float input {
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: var(--color-text);
    /* 16 px: an iPad in landscape gets this layout, and iOS zooms the page
       on focus into anything smaller. */
    font-size: 16px;
    outline: none;
  }

  .float input::placeholder {
    color: var(--color-muted);
  }

  .float:focus-within {
    outline: 2px solid var(--color-accent);
    outline-offset: 2px;
  }

  kbd {
    font: inherit;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-muted);
    border: 1px solid var(--color-divider);
    padding: 2px 7px;
  }
</style>
